import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { useState } from "react"
import { api } from "../../convex/_generated/api"
import { AdminCompanyActions } from "@/components/admin-company-actions"
import { AdminListPage, AdminRow } from "@/components/admin/admin-list-page"
import { AdminPersonLine } from "@/components/admin/admin-person-line"
import { CompanySocialLinks } from "@/components/company-social-links"
import { StatusPill } from "@/components/status-pill"
import { companyMetaParts } from "@/lib/company"

const MEMBER_ROLE_LABEL = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
} as const

type StatusFilter = "active" | "pending" | "approved" | "rejected"

const FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const

export const Route = createFileRoute("/admin/companies")({
  component: AdminCompaniesPage,
})

export function AdminCompaniesPage() {
  const [filter, setFilter] = useState<StatusFilter>("active")
  const companies = useQuery(
    api.admin.listCompanies,
    filter === "active" ? {} : { status: filter },
  )

  return (
    <AdminListPage
      title="Companies"
      description="Pending first, then approved. Rejected companies live on the Rejected filter."
      filter={{ value: filter, onChange: setFilter, options: FILTER_OPTIONS }}
      data={companies}
      emptyCopy="No companies match this filter."
      renderRow={(c) => (
        <AdminRow
          key={c._id}
          align="start"
          title={c.name || "(no company name)"}
          badges={
            <StatusPill
              domain="verification"
              status={c.verificationStatus ?? "pending"}
              as="badge"
            />
          }
          media={
            c.logoUrl && (
              <img
                src={c.logoUrl}
                alt=""
                className="size-12 rounded-lg border border-[color:var(--wf-line)] object-cover"
              />
            )
          }
          actions={
            <AdminCompanyActions
              companyId={c._id}
              verificationStatus={c.verificationStatus}
            />
          }
        >
          {c.oneLiner && (
            <p className="mt-1 text-sm font-medium text-[color:var(--wf-ink)]">
              {c.oneLiner}
            </p>
          )}
          <p className="mt-1 text-sm text-[color:var(--wf-mute)]">
            {c.memberCount} member{c.memberCount === 1 ? "" : "s"} · {c.openJobs}{" "}
            open job{c.openJobs === 1 ? "" : "s"}
          </p>
          {(c.joinedMembers?.length ?? 0) > 0 ? (
            <ul className="mt-1 space-y-0.5 text-sm text-[color:var(--wf-mute)]">
              {c.joinedMembers.map((member) => (
                <li key={member.profileId}>
                  <AdminPersonLine
                    role={MEMBER_ROLE_LABEL[member.role]}
                    name={member.name}
                    email={member.email}
                    linkedinUrl={member.linkedinUrl}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-1 text-sm text-[color:var(--wf-mute)]">
              <AdminPersonLine
                name={c.ownerName}
                email={c.ownerEmail}
                linkedinUrl={c.ownerLinkedinUrl}
              />
            </div>
          )}
          {(c.location || c.size || c.stage) && (
            <p className="mt-1 text-sm text-[color:var(--wf-mute)]">
              {companyMetaParts(c).join(" · ")}
            </p>
          )}
          {c.website && (
            <a
              href={c.website}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-sm text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"
            >
              {c.website}
            </a>
          )}
          {c.about && (
            <p className="mt-2 text-sm text-[color:var(--wf-mute)]">{c.about}</p>
          )}
          <CompanySocialLinks
            className="mt-2"
            company={{
              companyLinkedin: c.linkedin,
              companyTwitter: c.twitter,
              companyInstagram: c.instagram,
            }}
          />
        </AdminRow>
      )}
    />
  )
}
